import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OwnerGuard from "@/components/OwnerGuard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { WizardShell } from "@/components/wizard/WizardShell";
import { Step1Basics } from "@/components/wizard/steps/Step1Basics";
import { Step2Airbnb } from "@/components/wizard/steps/Step2Airbnb";
import { Step2bListingScan, getIncludedListingScanItems } from "@/components/wizard/steps/Step2bListingScan";
import { Step3Rooms } from "@/components/wizard/steps/Step3Rooms";
import { Step4Checklist } from "@/components/wizard/steps/Step4Checklist";
import { ChecklistMediaRecommendationsPanel } from "@/components/wizard/ChecklistMediaRecommendationsPanel";
import { Step5Inventory } from "@/components/wizard/steps/Step5Inventory";
import { Step6Calendar } from "@/components/wizard/steps/Step6Calendar";
import { Step7Team, type SentInviteChip } from "@/components/wizard/steps/Step7Team";
import { Step8Done } from "@/components/wizard/steps/Step8Done";
import { useAuth } from "@/contexts/AuthContext";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { setPendingTour } from "@/lib/tourState";
import { useCreatePropertyRoom } from "@/hooks/usePropertyRooms";
import { useCreateChecklist } from "@/hooks/useChecklists";
import { useCreateInventoryItem } from "@/hooks/useInventory";
import { useCreateProofStandard } from "@/hooks/useProofStandards";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import {
  ensurePropertyRooms,
  getOrCreateInventoryRoomId,
  type ListingScanDetectedItem,
  type PendingWizardCleaningTask,
  type PendingWizardProofStandard,
} from "@/lib/listingPhotoScan";
import {
  useCreateMemberInvite,
  sendMemberInviteEmail,
} from "@/hooks/useMemberInvites";
import {
  getDefaultPermissionForInviteRole,
  permissionToAccessLevel,
  PERMISSION_LABELS,
} from "@/components/owner/PermissionCards";
import type { Room, Task } from "@/types/checklist";
import {
  getChecklistTemplate,
  matchTemplateRoomToProperty,
} from "@/data/checklistTemplates";
import { normalizeRoomName } from "@/lib/roomUtils";
import {
  INVENTORY_TEMPLATES,
  inventoryScopeForCategory,
} from "@/data/inventoryTemplates";
import {
  clearWizardState,
  getDefaultRoomsForPropertyType,
  INITIAL_WIZARD_STATE,
  loadWizardState,
  saveWizardState,
  WIZARD_STEP_LABELS,
  parseListingScanPhotos,
  type ScrapedPhoto,
  type WizardState,
} from "@/lib/newPropertyWizard";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";
import {
  buildPropertyGalleryUrls,
  persistPropertyGalleryPhotos,
  syncPropertyGalleryFromReferencePhotos,
} from "@/lib/propertyGalleryPhotos";

type TaskSelection = { key: string; room: string; text: string; checked: boolean };

async function flushPendingProofStandards(
  propertyId: string,
  checklistId: string,
  pending: PendingWizardProofStandard[],
  createProofStandard: {
    mutateAsync: (args: {
      property_id: string;
      checklist_id: string;
      title: string;
      type: "proof_photo";
      room?: string | null;
      reference_media_urls?: string[];
      priority?: "high_value";
      display_order?: number;
    }) => Promise<unknown>;
  }
): Promise<number> {
  let order = 0;
  let count = 0;
  for (const p of pending) {
    if (!p.title?.trim()) continue;
    await createProofStandard.mutateAsync({
      property_id: propertyId,
      checklist_id: checklistId,
      title: p.title.trim(),
      type: "proof_photo",
      room: p.room?.trim() || null,
      reference_media_urls: p.referencePhotoUrl ? [p.referencePhotoUrl] : [],
      priority: "high_value",
      display_order: order++,
    });
    count += 1;
  }
  return count;
}

function parsePendingProofJson(json: string): PendingWizardProofStandard[] {
  try {
    const parsed = JSON.parse(json) as PendingWizardProofStandard[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parsePendingChecklistTasks(json: string): PendingWizardCleaningTask[] {
  try {
    const parsed = JSON.parse(json) as PendingWizardCleaningTask[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Build Room[] from AI-scanned cleaning tasks (grouped by room). */
function buildScannedTaskRooms(
  propertyRooms: string[],
  tasks: PendingWizardCleaningTask[]
): Room[] {
  const byRoom: Record<string, Task[]> = {};
  for (const t of tasks) {
    const roomName = matchTemplateRoomToProperty(t.room, propertyRooms);
    if (!byRoom[roomName]) byRoom[roomName] = [];
    byRoom[roomName].push({ text: t.taskText, task_type: "cleaning" });
  }
  return Object.entries(byRoom)
    .filter(([, tasks]) => tasks.length > 0)
    .map(([name, tasks]) => ({ name, tasks }));
}

/** Merge extra rooms/tasks into a base Room[] (adds to matching rooms, appends new ones). */
function mergeChecklistRooms(base: Room[], extra: Room[]): Room[] {
  const map = new Map<string, Task[]>();
  for (const r of base) map.set(r.name, [...r.tasks]);
  for (const r of extra) {
    const existing = map.get(r.name);
    if (existing) existing.push(...r.tasks);
    else map.set(r.name, [...r.tasks]);
  }
  return [...map.entries()]
    .filter(([, tasks]) => tasks.length > 0)
    .map(([name, tasks]) => ({ name, tasks }));
}

function flattenRoomsForMediaRecs(rooms: Room[]): { taskText: string; room: string }[] {
  const out: { taskText: string; room: string }[] = [];
  for (const room of rooms) {
    for (const task of room.tasks) {
      const text = typeof task === "string" ? task : task.text;
      if (text?.trim()) out.push({ taskText: text.trim(), room: room.name });
    }
  }
  return out;
}

function buildChecklistRooms(
  propertyRooms: string[],
  selections: TaskSelection[]
): Room[] {
  const byRoom: Record<string, Task[]> = {};
  for (const s of selections.filter((x) => x.checked)) {
    const roomName = matchTemplateRoomToProperty(s.room, propertyRooms);
    if (!byRoom[roomName]) byRoom[roomName] = [];
    byRoom[roomName].push({ text: s.text, task_type: "cleaning" });
  }
  return Object.entries(byRoom)
    .filter(([, tasks]) => tasks.length > 0)
    .map(([name, tasks]) => ({ name, tasks }));
}

function NewPropertyWizardContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const { can_create_properties, isLoading: capsLoading } = useUserCapabilities();
  const createRoom = useCreatePropertyRoom();
  const createChecklist = useCreateChecklist();
  const createInventoryItem = useCreateInventoryItem();
  const createProofStandard = useCreateProofStandard();
  const createMemberInvite = useCreateMemberInvite();

  const [state, setState] = useState<WizardState>(() => loadWizardState());
  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  const [savedForResume, setSavedForResume] = useState<WizardState | null>(null);
  const [checklistMode, setChecklistMode] = useState<"template" | "custom" | null>(null);
  const [inventoryMode, setInventoryMode] = useState<"recommended" | "manual" | null>(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  const [scrapedPhotos, setScrapedPhotos] = useState<ScrapedPhoto[]>([]);
  const [detectedAirbnbRooms, setDetectedAirbnbRooms] = useState<string[]>([]);
  // assignments: null roomName means "skip this photo"
  const [airbnbAssignments, setAirbnbAssignments] = useState<{ url: string; roomName: string | null }[]>([]);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sentInvites, setSentInvites] = useState<SentInviteChip[]>([]);
  const [inventorySelectionsJson, setInventorySelectionsJson] = useState(
    () => loadWizardState().inventorySelectionsJson
  );
  const [listingScanItems, setListingScanItems] = useState<ListingScanDetectedItem[]>([]);
  const [listingScanReviewReady, setListingScanReviewReady] = useState(false);
  const [showMediaRecs, setShowMediaRecs] = useState(false);
  const [mediaRecTasks, setMediaRecTasks] = useState<{ taskText: string; room: string }[]>([]);

  useEffect(() => {
    if (capsLoading) return;
    if (!can_create_properties) {
      toast({
        title: "Couldn’t add property",
        description: "Try again, or go back to Properties.",
        variant: "destructive",
      });
      navigate("/owner/properties", { replace: true });
    }
  }, [capsLoading, can_create_properties, navigate]);

  useEffect(() => {
    const saved = loadWizardState();
    if (saved.step > 1 && saved.propertyId) {
      setSavedForResume(saved);
      setResumePromptOpen(true);
    }
  }, []);

  const patchState = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveWizardState(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (state.step === 9) {
      clearWizardState();
    }
  }, [state.step]);

  useEffect(() => {
    if (state.step !== 3 || state.listingScanSkipped || state.listingScanDone) return;
    const photos = parseListingScanPhotos(state.listingScanPhotosJson);
    if (photos.length === 0) {
      patchState({ listingScanSkipped: true, step: 4 });
    }
  }, [state.step, state.listingScanPhotosJson, state.listingScanSkipped, state.listingScanDone, patchState]);

  const goToStep = (step: number) => patchState({ step });

  const handleDialogClose = (open: boolean) => {
    if (open) return;
    // Don't navigate immediately — show the exit confirmation first
    setExitConfirmOpen(true);
  };

  const discardWizardProgress = useCallback(async (propertyId: string | null) => {
    if (propertyId) {
      await supabase.from("properties").delete().eq("id", propertyId);
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    }
    clearWizardState();
    setState({ ...INITIAL_WIZARD_STATE });
    setCoverPhotoFile(null);
    setScrapedPhotos([]);
    setDetectedAirbnbRooms([]);
    setAirbnbAssignments([]);
    setInventorySelectionsJson("");
    setListingScanItems([]);
    setListingScanReviewReady(false);
    setChecklistMode(null);
    setInventoryMode(null);
    setSentInvites([]);
  }, [queryClient]);

  const handleConfirmExit = async () => {
    setExitConfirmOpen(false);
    await discardWizardProgress(state.propertyId);
    navigate("/owner/properties");
  };

  const handleBack = () => {
    if (state.step <= 1) {
      if (state.propertyId) {
        setExitConfirmOpen(true);
        return;
      }
      navigate("/owner/properties");
      return;
    }
    if (state.step === 4) {
      if (state.airbnbImportDone && !state.listingScanSkipped) {
        goToStep(3);
        return;
      }
      goToStep(2);
      return;
    }
    goToStep(state.step - 1);
  };

  const handleSkip = async () => {
    switch (state.step) {
      case 3:
        setListingScanItems([]);
        setListingScanReviewReady(false);
        patchState({ listingScanSkipped: true, listingScanDone: false, step: 4 });
        break;
      case 5:
        patchState({ checklistSkipped: true, checklistDone: false, step: 6 });
        break;
      case 6:
        patchState({ inventorySkipped: true, inventoryDone: false, step: 7 });
        break;
      case 7:
        patchState({ calendarSkipped: true, calendarDone: false, step: 8 });
        break;
      case 8:
        patchState({ teamSkipped: true, teamDone: false, step: 9 });
        break;
      default:
        break;
    }
  };

  const uploadCoverPhoto = async (): Promise<string | null> => {
    if (!coverPhotoFile || !user) return state.coverPhotoUrl ?? null;
    const fileExt = coverPhotoFile.name.split(".").pop();
    const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from("property-photos")
      .upload(filePath, coverPhotoFile);
    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from("property-photos").getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleStep1Continue = async () => {
    if (!state.name.trim()) {
      toast({ title: "Property name required", variant: "destructive" });
      return;
    }
    if (!user) return;

    setLoading(true);
    try {
      let photoUrl = state.coverPhotoUrl ?? null;
      if (coverPhotoFile) {
        photoUrl = await uploadCoverPhoto();
      }

      if (state.propertyId) {
        const { error } = await supabase
          .from("properties")
          .update({
            name: state.name.trim(),
            property_type: state.propertyType,
            address: state.address.trim() || null,
            unit: state.unit.trim() || null,
            city: state.city.trim() || null,
            state_province: state.state.trim() || null,
            country: state.country.trim() || null,
            ...(photoUrl
              ? { property_photo_url: photoUrl, property_photo_urls: [photoUrl] }
              : {}),
          })
          .eq("id", state.propertyId);
        if (error) throw error;
        patchState({ coverPhotoUrl: photoUrl, step: 2 });
        return;
      }

      const { data: property, error } = await supabase
        .from("properties")
        .insert({
          owner_id: user.id,
          name: state.name.trim(),
          property_type: state.propertyType,
          address: state.address.trim() || null,
          unit: state.unit.trim() || null,
          city: state.city.trim() || null,
          state_province: state.state.trim() || null,
          country: state.country.trim() || null,
          property_photo_url: photoUrl,
          ...(photoUrl ? { property_photo_urls: [photoUrl] } : {}),
        })
        .select()
        .single();

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["properties"] });
      patchState({
        propertyId: property.id,
        coverPhotoUrl: photoUrl,
        step: 2,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create property";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleAirbnbImport = async (url: string) => {
    if (!url.includes("airbnb.")) {
      toast({
        title: "Invalid URL",
        description: "Please enter an Airbnb listing URL",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-airbnb", {
        body: { phase: "preview", listingUrl: url },
      });

      let result = data as {
        ok: boolean;
        error?: string;
        photos?: ScrapedPhoto[];
        rooms?: string[];
      } | null;

      if (error) {
        if (error instanceof FunctionsHttpError && error.context) {
          try {
            result = (await (error.context as Response).json()) as typeof result;
          } catch {
            /* use generic message below */
          }
        }
        if (!result?.error) throw new Error(error.message);
      }

      if (!result?.ok) throw new Error(result?.error ?? "Could not fetch photos");
      const photos = result.photos ?? [];
      if (photos.length === 0) throw new Error("No room photos found in this listing.");

      const rooms =
        result.rooms ??
        ([...new Set(photos.map((p) => p.roomLabel).filter(Boolean))] as string[]);

      setScrapedPhotos(photos);
      setDetectedAirbnbRooms(rooms);
      setAirbnbAssignments([]);
      patchState({ airbnbUrl: url });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch listing";
      toast({ title: "Could not load listing", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSetup = () => {
    const defaults = getDefaultRoomsForPropertyType(state.propertyType);
    patchState({
      rooms: state.rooms.length > 0 ? state.rooms : defaults,
      listingScanSkipped: true,
      step: 4,
    });
  };

  const handleConfirmAirbnbAssignments = async () => {
    if (!state.propertyId) return;
    const toImport = airbnbAssignments.filter((a) => a.roomName !== null) as { url: string; roomName: string }[];

    setLoading(true);
    try {
      const scrapeUrls = toImport.map((a) => a.url);
      const galleryUrls = buildPropertyGalleryUrls(state.coverPhotoUrl, scrapeUrls);
      if (galleryUrls.length > 0) {
        await persistPropertyGalleryPhotos(state.propertyId, galleryUrls);
        queryClient.invalidateQueries({ queryKey: ["property", state.propertyId] });
      }

      if (toImport.length > 0) {
        const propertyId = state.propertyId;
        const coverUrl = state.coverPhotoUrl;
        // Import fires in the background; we don't block navigation on it
        supabase.functions
          .invoke("scrape-airbnb", {
            body: { phase: "import", propertyId, assignments: toImport },
          })
          .then(async ({ data }) => {
            const saved = (data as { saved?: number } | null)?.saved ?? 0;
            if (saved > 0) {
              toast({ title: `${saved} Airbnb photos imported`, description: "Available in Reference Photos." });
              try {
                await syncPropertyGalleryFromReferencePhotos(propertyId, coverUrl);
                queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
              } catch {
                /* gallery already has scrape URLs */
              }
            }
          })
          .catch(() => {});
      }

      const listingScanPhotosJson = JSON.stringify(
        toImport.map((a) => ({ url: a.url, roomName: a.roomName }))
      );

      setScrapedPhotos([]);
      setAirbnbAssignments([]);
      setDetectedAirbnbRooms([]);
      setListingScanItems([]);
      setListingScanReviewReady(false);
      patchState({
        airbnbImportDone: true,
        listingScanPhotosJson,
        listingScanSkipped: false,
        listingScanDone: false,
        step: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Continue = async () => {
    if (!state.propertyId) return;
    if (state.rooms.length === 0) {
      patchState({ step: 5 });
      return;
    }
    setLoading(true);
    try {
      const { data: existing } = await supabase
        .from("property_rooms")
        .select("name_normalized, name")
        .eq("property_id", state.propertyId)
        .is("archived_at", null);

      const existingNorm = new Set((existing ?? []).map((r) => r.name_normalized));

      for (const roomName of state.rooms) {
        const norm = normalizeRoomName(roomName.trim());
        if (existingNorm.has(norm)) continue;
        try {
          await createRoom.mutateAsync({
            propertyId: state.propertyId,
            name: roomName.trim(),
          });
        } catch (e) {
          if (!(e instanceof Error && e.message.includes("already exists"))) {
            throw e;
          }
        }
      }

      patchState({ step: 5 });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save rooms";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleListingScanContinue = async () => {
    if (!state.propertyId) return;

    const included = getIncludedListingScanItems(listingScanItems);
    if (included.length === 0) {
      patchState({ listingScanDone: true, listingScanSkipped: false, step: 4 });
      return;
    }

    setLoading(true);
    try {
      await ensurePropertyRooms(state.propertyId, state.rooms);

      const pendingProof: PendingWizardProofStandard[] = [];
      const pendingCleaningTasks: PendingWizardCleaningTask[] = [];
      let inventoryCount = 0;

      for (const item of included) {
        if (item.destination === "unit_check" || item.destination === "maintenance") {
          // Both land in Unit Check (photo-verify proof standards)
          pendingProof.push({
            title: item.name.trim(),
            room: item.roomName.trim(),
            referencePhotoUrl: item.sourcePhotoUrl,
          });
          continue;
        }

        if (item.destination === "cleaning_task") {
          pendingCleaningTasks.push({
            taskText: item.suggestedTask || `Clean ${item.name.toLowerCase()}`,
            room: item.roomName.trim(),
          });
          continue;
        }

        // supply_closet / guest_amenities → inventory
        const scope =
          item.destination === "guest_amenities" ? "guest_amenities" : ("supply_closet" as const);
        const roomId = await getOrCreateInventoryRoomId(state.propertyId, item.roomName);
        await createInventoryItem.mutateAsync({
          propertyId: state.propertyId,
          itemName: item.name.trim(),
          thresholdCount: 1,
          currentCount: 1,
          roomId,
          inventoryScope: scope,
        });
        inventoryCount += 1;
      }

      await invalidateInventoryQueries(queryClient, state.propertyId);

      const mergedProof = [...parsePendingProofJson(state.pendingProofStandardsJson), ...pendingProof];
      const mergedTasks = [...parsePendingChecklistTasks(state.pendingChecklistTasksJson), ...pendingCleaningTasks];

      patchState({
        listingScanDone: true,
        listingScanSkipped: false,
        pendingProofStandardsJson: JSON.stringify(mergedProof),
        pendingChecklistTasksJson: JSON.stringify(mergedTasks),
        inventoryItemCount: state.inventoryItemCount + inventoryCount,
        inventoryDone: inventoryCount > 0 ? true : state.inventoryDone,
        step: 4,
      });

      const parts: string[] = [];
      if (pendingCleaningTasks.length > 0) parts.push(`${pendingCleaningTasks.length} cleaning task${pendingCleaningTasks.length !== 1 ? "s" : ""}`);
      if (pendingProof.length > 0) parts.push(`${pendingProof.length} Unit Check item${pendingProof.length !== 1 ? "s" : ""}`);
      if (inventoryCount > 0) parts.push(`${inventoryCount} inventory item${inventoryCount !== 1 ? "s" : ""}`);
      if (parts.length > 0) {
        toast({ title: "Scan saved", description: parts.join(" · ") + " — added to your checklist and inventory." });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save scanned items";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleStep5Continue = async () => {
    if (!state.propertyId) return;
    if (state.checklistDone && showMediaRecs) return;
    if (state.checklistDone) {
      patchState({ step: 6 });
      return;
    }

    if (checklistMode === "custom") {
      const pendingProof = parsePendingProofJson(state.pendingProofStandardsJson);
      const pendingTasks = parsePendingChecklistTasks(state.pendingChecklistTasksJson);
      const scannedRooms = buildScannedTaskRooms(state.rooms, pendingTasks);
      if (pendingProof.length > 0 || scannedRooms.length > 0) {
        setLoading(true);
        try {
          const checklist = await createChecklist.mutateAsync({
            propertyId: state.propertyId,
            name: "Turnover checklist",
            type: "cleaning",
            description: "Checklist built from your listing scan",
            rooms: scannedRooms,
            isDefault: true,
          });
          await flushPendingProofStandards(
            state.propertyId,
            checklist.id,
            pendingProof,
            createProofStandard
          );
          const tasksForRecs = flattenRoomsForMediaRecs(scannedRooms);
          setMediaRecTasks(tasksForRecs);
          setShowMediaRecs(tasksForRecs.length > 0);
          patchState({
            checklistSkipped: false,
            checklistDone: true,
            pendingProofStandardsJson: "[]",
            pendingChecklistTasksJson: "[]",
            ...(tasksForRecs.length === 0 ? { step: 6 as const } : {}),
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to save checklist items";
          toast({ title: "Error", description: msg, variant: "destructive" });
        } finally {
          setLoading(false);
        }
        return;
      }
      patchState({ checklistSkipped: true, checklistDone: false, step: 6 });
      return;
    }

    if (!state.selectedTemplate) {
      toast({ title: "Select a checklist template", variant: "destructive" });
      return;
    }
    const template = getChecklistTemplate(state.selectedTemplate);
    if (!template) return;

    let selections: TaskSelection[] = [];
    try {
      selections = JSON.parse(state.customTasksJson) as TaskSelection[];
    } catch {
      selections = [];
    }
    const checked = selections.filter((s) => s.checked);
    if (checked.length === 0) {
      toast({ title: "Select at least one task", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const templateRooms = buildChecklistRooms(state.rooms, checked);
      const scannedTaskRooms = buildScannedTaskRooms(
        state.rooms,
        parsePendingChecklistTasks(state.pendingChecklistTasksJson)
      );
      const rooms = mergeChecklistRooms(templateRooms, scannedTaskRooms);
      const checklist = await createChecklist.mutateAsync({
        propertyId: state.propertyId,
        name: template.name,
        type: "cleaning",
        description: template.description,
        rooms,
        isDefault: true,
      });

      const pendingProof = parsePendingProofJson(state.pendingProofStandardsJson);
      await flushPendingProofStandards(
        state.propertyId,
        checklist.id,
        pendingProof,
        createProofStandard
      );

      const tasksForRecs = flattenRoomsForMediaRecs(rooms);
      setMediaRecTasks(tasksForRecs);
      setShowMediaRecs(tasksForRecs.length > 0);
      patchState({
        checklistDone: true,
        checklistSkipped: false,
        checklistTaskCount: checked.length,
        pendingProofStandardsJson: "[]",
        pendingChecklistTasksJson: "[]",
      });
      if (tasksForRecs.length === 0) {
        patchState({ step: 6 });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create checklist";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleStep6Continue = async () => {
    if (!state.propertyId) return;

    if (inventoryMode === "manual") {
      patchState({ inventorySkipped: true, inventoryDone: false, step: 7 });
      return;
    }

    type InvSel = { name: string; checked: boolean; defaultQty: number };
    let selections: InvSel[] = [];
    try {
      selections = JSON.parse(inventorySelectionsJson) as InvSel[];
    } catch {
      selections = [];
    }
    const toCreate = selections.filter((s) => s.checked);

    setLoading(true);
    try {
      for (const sel of toCreate) {
        const meta = INVENTORY_TEMPLATES.find((t) => t.name === sel.name);
        if (!meta) continue;
        await createInventoryItem.mutateAsync({
          propertyId: state.propertyId,
          itemName: sel.name,
          thresholdCount: sel.defaultQty,
          currentCount: sel.defaultQty,
          inventoryScope: inventoryScopeForCategory(meta.category),
        });
      }

      patchState({
        inventoryDone: toCreate.length > 0,
        inventorySkipped: false,
        inventoryItemCount: toCreate.length,
        inventorySelectionsJson,
        step: 7,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create inventory";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    if (!state.propertyId || !state.icalUrl.trim()) return;
    setLoading(true);
    try {
      const { error: updateError } = await supabase
        .from("properties")
        .update({ airbnb_ical_url: state.icalUrl.trim() })
        .eq("id", state.propertyId);
      if (updateError) throw updateError;

      const { error: syncError } = await supabase.functions.invoke("sync-ical", {
        body: { propertyId: state.propertyId },
      });
      if (syncError) throw syncError;

      queryClient.invalidateQueries({ queryKey: ["property", state.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", state.propertyId] });

      patchState({ calendarDone: true, calendarSkipped: false });
      toast({ title: "Calendar connected", description: "Your booking calendar is syncing." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to connect calendar";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!state.propertyId || !user || !state.inviteEmail.trim()) return;

    setLoading(true);
    try {
      const inviteRole = state.inviteRole;
      const permission = getDefaultPermissionForInviteRole(inviteRole);
      const accessLevel = permissionToAccessLevel(permission);

      const invite = await createMemberInvite.mutateAsync({
        ownerId: user.id,
        email: state.inviteEmail.trim(),
        inviteRole,
        propertyId: state.propertyId,
        accessLevel,
      });

      const emailResult = await sendMemberInviteEmail({
        email: state.inviteEmail.trim(),
        token: invite.token,
        invite_role: inviteRole,
        owner_name: profile?.full_name ?? "Your host",
        property_name: state.name,
        properties: [
          {
            name: state.name.trim() || "Property",
            photo_url: state.coverPhotoUrl?.trim() || null,
          },
        ],
        permission_label: PERMISSION_LABELS[permission],
      });

      if (!emailResult.ok) {
        toast({
          title: "Invite created",
          description: "Email could not be sent — share the invite link from Access & Team.",
        });
      } else {
        toast({ title: "Invite sent", description: `Invitation emailed to ${state.inviteEmail.trim()}` });
      }

      const roleLabel = inviteRole === "company_admin" ? "Cleaning Company" : "Cleaner";
      setSentInvites((prev) => [...prev, { email: state.inviteEmail.trim(), roleLabel }]);
      const count = state.teamInviteCount + 1;
      patchState({
        inviteEmail: "",
        teamDone: true,
        teamInviteCount: count,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send invite";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    switch (state.step) {
      case 1:
        await handleStep1Continue();
        break;
      case 2:
        if (state.airbnbImportDone) {
          goToStep(3);
          break;
        }
        if (scrapedPhotos.length > 0) {
          await handleConfirmAirbnbAssignments();
        } else {
          handleManualSetup();
        }
        break;
      case 3:
        await handleListingScanContinue();
        break;
      case 4:
        await handleStep3Continue();
        break;
      case 5:
        await handleStep5Continue();
        break;
      case 6:
        await handleStep6Continue();
        break;
      case 7:
        if (state.calendarDone) goToStep(8);
        else patchState({ step: 8 });
        break;
      case 8:
        patchState({ teamSkipped: !state.teamDone, step: 9 });
        break;
      default:
        break;
    }
  };

  const stepLabel = WIZARD_STEP_LABELS[state.step - 1] ?? "";
  const isOptional =
    state.step === 3 || (state.step >= 5 && state.step <= 8);

  const nextDisabled =
    (state.step === 1 && !state.name.trim()) ||
    (state.step === 3 && !listingScanReviewReady) ||
    (state.step === 4 && state.rooms.length === 0) ||
    (state.step === 5 && showMediaRecs) ||
    (state.step === 5 &&
      checklistMode !== "custom" &&
      (checklistMode !== "template" || !state.selectedTemplate)) ||
    (state.step === 6 &&
      inventoryMode !== "manual" &&
      inventoryMode !== "recommended") ||
    (state.step === 2 &&
      scrapedPhotos.length > 0 &&
      !state.airbnbImportDone &&
      airbnbAssignments.filter((a) => a.roomName !== null).length === 0);

  const renderStep = () => {
    switch (state.step) {
      case 1:
        return (
          <Step1Basics
            state={state}
            coverPhotoFile={coverPhotoFile}
            onChange={patchState}
            onCoverPhotoChange={setCoverPhotoFile}
          />
        );
      case 2:
        return (
          <Step2Airbnb
            state={state}
            scrapedPhotos={scrapedPhotos}
            detectedRooms={detectedAirbnbRooms}
            assignments={airbnbAssignments}
            onChange={patchState}
            onImportAirbnb={handleAirbnbImport}
            onManualSetup={handleManualSetup}
            onAssignmentsChange={setAirbnbAssignments}
            onDetectedRoomsChange={setDetectedAirbnbRooms}
            loading={loading}
          />
        );
      case 3:
        return state.propertyId ? (
          <Step2bListingScan
            propertyId={state.propertyId}
            photos={parseListingScanPhotos(state.listingScanPhotosJson)}
            items={listingScanItems}
            onItemsChange={setListingScanItems}
            onReviewReady={setListingScanReviewReady}
          />
        ) : null;
      case 4:
        return (
          <Step3Rooms
            rooms={state.rooms}
            scrapedPhotos={scrapedPhotos}
            onChange={(rooms) => patchState({ rooms })}
          />
        );
      case 5:
        if (state.checklistDone && showMediaRecs && state.propertyId) {
          return (
            <ChecklistMediaRecommendationsPanel
              propertyId={state.propertyId}
              tasks={mediaRecTasks}
              onDone={() => {
                setShowMediaRecs(false);
                patchState({ step: 6 });
              }}
            />
          );
        }
        return (
          <Step4Checklist
            propertyRooms={state.rooms}
            selectedTemplate={state.selectedTemplate}
            customTasksJson={state.customTasksJson}
            propertyType={state.propertyType}
            onSelectTemplate={(id) =>
              patchState({ selectedTemplate: id, customTasksJson: "" })
            }
            onTasksChange={(json) => patchState({ customTasksJson: json })}
            onModeChange={setChecklistMode}
          />
        );
      case 6:
        return (
          <Step5Inventory
            selectionsJson={inventorySelectionsJson}
            onSelectionsChange={(json) => {
              setInventorySelectionsJson(json);
              patchState({ inventorySelectionsJson: json });
            }}
            onModeChange={setInventoryMode}
          />
        );
      case 7:
        return (
          <Step6Calendar
            icalUrl={state.icalUrl}
            onChange={(url) => patchState({ icalUrl: url })}
            onConnect={handleConnectCalendar}
            loading={loading}
            connected={state.calendarDone}
          />
        );
      case 8:
        return (
          <Step7Team
            email={state.inviteEmail}
            inviteRole={state.inviteRole}
            sentInvites={sentInvites}
            onEmailChange={(inviteEmail) => patchState({ inviteEmail })}
            onRoleChange={(inviteRole) => patchState({ inviteRole })}
            onSendInvite={handleSendInvite}
            loading={loading}
          />
        );
      case 9:
        return (
          <Step8Done
            state={state}
            onViewProperty={async () => {
              if (state.propertyId) {
                const scrapeUrls = parseListingScanPhotos(state.listingScanPhotosJson).map((p) => p.url);
                const galleryUrls = buildPropertyGalleryUrls(state.coverPhotoUrl, scrapeUrls);
                if (galleryUrls.length > 0) {
                  try {
                    await persistPropertyGalleryPhotos(state.propertyId, galleryUrls);
                    queryClient.invalidateQueries({ queryKey: ["property", state.propertyId] });
                  } catch {
                    /* non-blocking */
                  }
                }
              }
              clearWizardState();
              if (state.propertyId) {
                setPendingTour("owner-property-detail");
                navigate(`/properties/${state.propertyId}`);
              }
            }}
            onAddAnother={() => {
              clearWizardState();
              setState({ ...INITIAL_WIZARD_STATE });
              setCoverPhotoFile(null);
              setScrapedPhotos([]);
              setDetectedAirbnbRooms([]);
              setAirbnbAssignments([]);
              setSentInvites([]);
              setInventorySelectionsJson("");
              setChecklistMode(null);
              setInventoryMode(null);
              navigate("/properties/new", { replace: true });
            }}
          />
        );
      default:
        return null;
    }
  };

  if (capsLoading || !can_create_properties) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <AlertDialog open={resumePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Continue where you left off?</AlertDialogTitle>
            <AlertDialogDescription>
              You have an unfinished setup for &ldquo;{savedForResume?.name || "your property"}
              &rdquo; at step {savedForResume?.step ?? 1} of 9.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={async () => {
                await discardWizardProgress(savedForResume?.propertyId ?? null);
                setResumePromptOpen(false);
              }}
            >
              Start fresh
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => setResumePromptOpen(false)}>
              Continue setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={exitConfirmOpen} onOpenChange={setExitConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel property setup?</AlertDialogTitle>
            <AlertDialogDescription>
              {state.propertyId
                ? "This will delete the property and discard everything you've entered. You can always add a new property later."
                : "Any information you've entered will be discarded."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setExitConfirmOpen(false)}>
              Continue setup
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmExit}
            >
              Cancel setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!resumePromptOpen && (
        <Dialog open onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-3xl w-full h-[90vh] overflow-hidden flex flex-col p-0 sm:max-w-3xl">
            <WizardShell
              currentStep={state.step}
              totalSteps={9}
              stepLabels={WIZARD_STEP_LABELS}
              stepLabel={stepLabel}
              isOptional={isOptional}
              onBack={handleBack}
              onNext={handleNext}
              onSkip={isOptional ? handleSkip : undefined}
              nextDisabled={nextDisabled}
              nextLoading={loading}
              nextLabel={
                state.step === 2 && scrapedPhotos.length > 0 && !state.airbnbImportDone
                  ? `Import ${airbnbAssignments.filter((a) => a.roomName !== null).length} Photos`
                  : state.step === 3
                  ? "Save & continue"
                  : state.step === 7 && !state.calendarDone
                  ? "Continue"
                  : undefined
              }
            >
              {renderStep()}
            </WizardShell>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default function NewPropertyWizard() {
  return (
    <OwnerGuard>
      <NewPropertyWizardContent />
    </OwnerGuard>
  );
}
